import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";

export interface Profile {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  phone: string | null;
  location: string | null;
  website: string | null;
  linkedin_url: string | null;
  years_experience: number | null;
  job_title: string | null;
  industry: string | null;
  onboarding_completed: boolean;
  onboarding_step: number;
  email_verified_at: string | null;
  last_seen_at: string;
  failed_login_attempts: number;
  locked_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: 'admin' | 'user';
  assigned_at: string;
  assigned_by: string | null;
}

export interface AuthState {
  user: User | null;
  profile: Profile | null;
  roles: UserRole[];
  isAdmin: boolean;
  isEmailVerified: boolean;
  onboardingStatus: 'new' | 'partial' | 'complete';
}

export class AuthService {
  // Core Authentication
  static async signUp(email: string, password: string, metadata?: { first_name?: string; last_name?: string }) {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: metadata
        }
      });
      
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  static async signIn(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      // Reset failed login attempts on successful login (after auth, RLS allows it)
      if (data?.user && !error) {
        this.resetFailedLoginAttempts(data.user.id).catch(() => {});
      }

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  static async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      return { error };
    }
  }

  static async resetPassword(email: string) {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=reset-password`
      });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  static async updatePassword(newPassword: string) {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Session Management
  static async getCurrentUser(): Promise<{ user: User | null; session: Session | null }> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      return { user: session?.user ?? null, session };
    } catch (error) {
      return { user: null, session: null };
    }
  }

  static async refreshSession() {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Profile Management
  static async getProfile(userId: string): Promise<{ profile: Profile | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      return { profile: data, error };
    } catch (error) {
      return { profile: null, error };
    }
  }

  static async updateProfile(userId: string, updates: Partial<Profile>): Promise<{ profile: Profile | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      return { profile: data, error };
    } catch (error) {
      return { profile: null, error };
    }
  }

  // Role Management
  static async getUserRoles(userId: string): Promise<{ roles: UserRole[]; error: any }> {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId);

      return { roles: data || [], error };
    } catch (error) {
      return { roles: [], error };
    }
  }

  static async hasRole(userId: string, role: 'admin' | 'user'): Promise<{ hasRole: boolean; error: any }> {
    try {
      const { data, error } = await supabase
        .rpc('has_role', { _user_id: userId, _role: role });

      return { hasRole: data || false, error };
    } catch (error) {
      return { hasRole: false, error };
    }
  }

  static async isAdmin(userId: string): Promise<{ isAdmin: boolean; error: any }> {
    try {
      const { data, error } = await supabase
        .rpc('is_admin', { _user_id: userId });

      return { isAdmin: data || false, error };
    } catch (error) {
      return { isAdmin: false, error };
    }
  }

  // Avatar Management
  static async uploadAvatar(userId: string, file: File): Promise<{ url: string | null; error: any }> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/avatar.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { 
          upsert: true,
          contentType: file.type
        });

      if (error) {
        return { url: null, error };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      return { url: publicUrl, error: null };
    } catch (error) {
      return { url: null, error };
    }
  }

  // Email Verification
  static async resendEmailVerification() {
    try {
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: (await supabase.auth.getUser()).data.user?.email || ''
      });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  static async markEmailAsVerified(userId: string) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ email_verified_at: new Date().toISOString() })
        .eq('id', userId)
        .select()
        .single();

      return { profile: data, error };
    } catch (error) {
      return { profile: null, error };
    }
  }

  // Security Features
  static async incrementFailedLoginAttempts(email: string) {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('failed_login_attempts')
        .eq('email', email)
        .single();

      const newCount = (profile?.failed_login_attempts || 0) + 1;
      const lockUntil = newCount >= 5 ? new Date(Date.now() + 30 * 60 * 1000) : null; // 30 min lockout

      await supabase
        .from('profiles')
        .update({ 
          failed_login_attempts: newCount,
          locked_until: lockUntil?.toISOString() || null
        })
        .eq('email', email);

      return { attempts: newCount, lockedUntil: lockUntil };
    } catch (error) {
      console.error('Failed to increment login attempts:', error);
      return { attempts: 0, lockedUntil: null };
    }
  }

  static async resetFailedLoginAttempts(userId: string) {
    try {
      await supabase
        .from('profiles')
        .update({ 
          failed_login_attempts: 0,
          locked_until: null,
          last_seen_at: new Date().toISOString()
        })
        .eq('id', userId);
    } catch (error) {
      console.error('Failed to reset login attempts:', error);
    }
  }

  // Auth State Management
  static onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback);
  }

  // Utility Functions
  static getOnboardingStatus(profile: Profile | null): 'new' | 'partial' | 'complete' {
    if (!profile) return 'new';
    
    if (profile.onboarding_completed) return 'complete';
    
    const hasBasicInfo = profile.first_name && profile.last_name && profile.display_name;
    if (hasBasicInfo) return 'partial';
    
    return 'new';
  }

  static isEmailVerified(profile: Profile | null): boolean {
    return Boolean(profile?.email_verified_at);
  }

  static async getFullAuthState(userId: string): Promise<AuthState> {
    try {
      const [profileResult, rolesResult, adminResult] = await Promise.all([
        this.getProfile(userId),
        this.getUserRoles(userId),
        this.isAdmin(userId)
      ]);

      const { user } = await this.getCurrentUser();

      return {
        user,
        profile: profileResult.profile,
        roles: rolesResult.roles,
        isAdmin: adminResult.isAdmin,
        isEmailVerified: this.isEmailVerified(profileResult.profile),
        onboardingStatus: this.getOnboardingStatus(profileResult.profile)
      };
    } catch (error) {
      return {
        user: null,
        profile: null,
        roles: [],
        isAdmin: false,
        isEmailVerified: false,
        onboardingStatus: 'new'
      };
    }
  }
}